import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type {
  InvestmentProduct,
  CreateProductInput,
  UpdateProductInput,
  ProductFilters,
  SimulationParams,
  SimulationResult,
  InvestmentApplication,
  ApplyInput,
  ApplicationFilters,
  Redemption,
  RedeemInput,
  RedemptionCalculation,
  Portfolio,
  PortfolioHistory,
  InvestorProfile,
  SuitabilityQuestion,
  QuestionnaireAnswer,
  SuitabilityResult,
  InvestmentConfig,
  UpdateInvestmentConfigInput,
  InvestmentStatistics,
} from '../types/investments';

// ==================== Products ====================

export function useInvestmentProducts(filters?: ProductFilters) {
  return useQuery({
    queryKey: ['investment-products', filters],
    queryFn: () =>
      api
        .get<{ products: InvestmentProduct[]; total: number }>('/investments/products', {
          params: filters,
        })
        .then((r) => r.data),
  });
}

export function useInvestmentProduct(productId: string) {
  return useQuery({
    queryKey: ['investment-product', productId],
    queryFn: () =>
      api.get<InvestmentProduct>(`/investments/products/${productId}`).then((r) => r.data),
    enabled: !!productId,
  });
}

export function useCreateInvestmentProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateProductInput) =>
      api.post<InvestmentProduct>('/investments/products', data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['investment-products'] });
    },
  });
}

export function useUpdateInvestmentProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateProductInput }) =>
      api.put<InvestmentProduct>(`/investments/products/${id}`, data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['investment-products'] });
    },
  });
}

export function useToggleProductAvailability() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, available }: { id: string; available: boolean }) =>
      api
        .patch<InvestmentProduct>(`/investments/products/${id}/availability`, { available })
        .then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['investment-products'] });
    },
  });
}

// ==================== Simulation ====================

export function useInvestmentSimulation() {
  return useMutation({
    mutationFn: (params: SimulationParams) =>
      api.post<SimulationResult>('/investments/simulate', params).then((r) => r.data),
  });
}

// ==================== Applications ====================

export function useInvestmentApplications(filters?: ApplicationFilters) {
  return useQuery({
    queryKey: ['investment-applications', filters],
    queryFn: () =>
      api
        .get<{ applications: InvestmentApplication[]; total: number }>(
          '/investments/applications',
          { params: filters }
        )
        .then((r) => r.data),
  });
}

export function useMyInvestmentApplications(filters?: ApplicationFilters) {
  return useQuery({
    queryKey: ['my-investment-applications', filters],
    queryFn: () =>
      api
        .get<{ applications: InvestmentApplication[]; total: number }>(
          '/investments/applications/my',
          { params: filters }
        )
        .then((r) => r.data),
  });
}

export function useInvestmentApplication(applicationId: string) {
  return useQuery({
    queryKey: ['investment-application', applicationId],
    queryFn: () =>
      api
        .get<InvestmentApplication>(`/investments/applications/${applicationId}`)
        .then((r) => r.data),
    enabled: !!applicationId,
  });
}

export function useApplyInvestment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ApplyInput) =>
      api.post<InvestmentApplication>('/investments/applications', data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['investment-applications'] });
      queryClient.invalidateQueries({ queryKey: ['my-investment-applications'] });
      queryClient.invalidateQueries({ queryKey: ['portfolio'] });
    },
  });
}

// ==================== Redemptions ====================

export function useRedemptions(filters?: { status?: string; page?: number; limit?: number }) {
  return useQuery({
    queryKey: ['redemptions', filters],
    queryFn: () =>
      api
        .get<{ redemptions: Redemption[]; total: number }>('/investments/redemptions', {
          params: filters,
        })
        .then((r) => r.data),
  });
}

export function useRedemption(redemptionId: string) {
  return useQuery({
    queryKey: ['redemption', redemptionId],
    queryFn: () =>
      api.get<Redemption>(`/investments/redemptions/${redemptionId}`).then((r) => r.data),
    enabled: !!redemptionId,
  });
}

export function useCalculateRedemption(applicationId: string, amount?: number) {
  return useQuery({
    queryKey: ['redemption-calculation', applicationId, amount],
    queryFn: () =>
      api
        .post<RedemptionCalculation>(
          `/investments/applications/${applicationId}/calculate-redemption`,
          { amount }
        )
        .then((r) => r.data),
    enabled: !!applicationId,
  });
}

export function useRedeemInvestment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      applicationId,
      data,
    }: {
      applicationId: string;
      data?: RedeemInput;
    }) =>
      api
        .post<Redemption>(`/investments/applications/${applicationId}/redeem`, data)
        .then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['investment-applications'] });
      queryClient.invalidateQueries({ queryKey: ['my-investment-applications'] });
      queryClient.invalidateQueries({ queryKey: ['portfolio'] });
      queryClient.invalidateQueries({ queryKey: ['redemptions'] });
    },
  });
}

// ==================== Portfolio ====================

export function usePortfolio() {
  return useQuery({
    queryKey: ['portfolio'],
    queryFn: () => api.get<Portfolio>('/investments/portfolio').then((r) => r.data),
  });
}

export function usePortfolioHistory(period?: '7d' | '30d' | '90d' | '1y') {
  return useQuery({
    queryKey: ['portfolio-history', period],
    queryFn: () =>
      api
        .get<PortfolioHistory[]>('/investments/portfolio/history', { params: { period } })
        .then((r) => r.data),
  });
}

// ==================== Suitability / Investor Profile ====================

export function useInvestorProfile() {
  return useQuery({
    queryKey: ['investor-profile'],
    queryFn: () => api.get<InvestorProfile>('/investments/profile').then((r) => r.data),
  });
}

export function useSuitabilityQuestionnaire() {
  return useQuery({
    queryKey: ['suitability-questionnaire'],
    queryFn: () =>
      api.get<SuitabilityQuestion[]>('/investments/profile/questionnaire').then((r) => r.data),
  });
}

export function useSubmitSuitability() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (answers: QuestionnaireAnswer[]) =>
      api.post<InvestorProfile>('/investments/profile', { answers }).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['investor-profile'] });
    },
  });
}

export function useValidateSuitability(productId: string) {
  return useQuery({
    queryKey: ['suitability-validation', productId],
    queryFn: () =>
      api
        .get<SuitabilityResult>(`/investments/profile/validate/${productId}`)
        .then((r) => r.data),
    enabled: !!productId,
  });
}

// ==================== Config (Admin) ====================

export function useInvestmentConfig() {
  return useQuery({
    queryKey: ['investment-config'],
    queryFn: () =>
      api.get<InvestmentConfig>('/investments/admin/config').then((r) => r.data),
  });
}

export function useUpdateInvestmentConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateInvestmentConfigInput) =>
      api.put<InvestmentConfig>('/investments/admin/config', data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['investment-config'] });
    },
  });
}

// ==================== Statistics (Admin) ====================

export function useInvestmentStatistics() {
  return useQuery({
    queryKey: ['investment-statistics'],
    queryFn: () =>
      api.get<InvestmentStatistics>('/investments/admin/statistics').then((r) => r.data),
  });
}
